import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';
import tracking from 'dibs-tracking';

// components
import { SbSharedLink } from '../SbSharedLink/SbSharedLink';
import { SbSharedCategoryTilesTracking } from '../SbSharedCategoryTilesTracking/SbSharedCategoryTilesTracking';
import { SbRespTilesSwiper } from '../SbRespTilesSwiper/SbRespTilesSwiper';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

// utils
import { getSrcsetString } from '../../utils/srcSet';
import { createNewUriRef, getQueryParam } from '../../utils/uriUtils';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// styles
import styles from './sbRespCategoryTiles.scss';

//constants
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import { SORT } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';

export const SbRespCategoryTilesComponent = props => {
    const [hasFiredAbTestTracking, setHasFiredAbTestTracking] = useState(false);
    const { isSmallerSbCategoryTilesVariant, isSbTopNavVariant } = useContext(ServerVarsContext);

    useEffect(() => {
        if (!hasFiredAbTestTracking) {
            setHasFiredAbTestTracking(true);
            const trackingData = getAbTestTrackingData(document.cookie, 'SMALLER_FILTER_TILES');
            if (trackingData && trackingData.testGroup) {
                tracking.trackAbTestEvent(trackingData, 'SEARCH_BROWSE_DESKTOP');
            }
        }
    }, [hasFiredAbTestTracking, setHasFiredAbTestTracking]);

    const { itemSearch } = props;
    const { displayUriRef } = itemSearch;

    /**
     * We show 5 tiles max.
     *
     * Special case - for fashion we only show 4, but that is already handled in graphql, so we
     * don't need to handle it here.
     *
     */

    // Max tiles counter is increased for the test ENGAGE-1541.
    const maxTilesCount = isSbTopNavVariant ? 10 : 5;
    const tiles = itemSearch?.categoryTiles?.slice(0, maxTilesCount) || [];

    const refetchInFlight = useSelector(state => state.filters.refetchInFlight);
    const srcSets = tiles.map(tile => getSrcsetString(tile.masterImage));
    const sort = getQueryParam(displayUriRef, SORT);
    if (!tiles.length) {
        return null;
    }
    return (
        <ServerVarsContextConsumer>
            {({ isTablet }) => {
                const containerClass = classnames(styles.container, {
                    rowFlex: !isTablet,
                    [styles.smallerContainer]: isSmallerSbCategoryTilesVariant,
                    [styles.isSbTopNavVariant]: isSbTopNavVariant,
                    [styles.loading]: refetchInFlight,
                });
                return (
                    <SbSharedCategoryTilesTracking itemSearch={itemSearch} maxTilesAllowed={5}>
                        {({ trackClick }) => (
                            <div className={containerClass} data-tn="category-tiles">
                                <SbRespTilesSwiper
                                    displayUriRef={displayUriRef}
                                    isTablet={isTablet}
                                >
                                    {tiles.map((categoryTile, index) => {
                                        const { displayName, url, image } = categoryTile;
                                        // to persist the sort query in the category tile link
                                        const href = sort ? createNewUriRef(url, { sort }) : url;
                                        const itemContainerClasses = classnames(
                                            styles.itemContainer,
                                            'colXs3',
                                            {
                                                // For some css reason "nth-child" directive is not overwritten,
                                                // so setting either one or another limit class.
                                                [styles.itemLimit]: !isSbTopNavVariant,
                                                [styles.sbTopNavVariantItemLimit]: isSbTopNavVariant,
                                            }
                                        );

                                        return (
                                            <SbSharedLink
                                                className={itemContainerClasses}
                                                href={href}
                                                onClick={() => trackClick(index)}
                                                key={displayName}
                                            >
                                                <div className={styles.imageWrapper}>
                                                    <img
                                                        className={styles.image}
                                                        src={image}
                                                        alt={displayName}
                                                        srcSet={srcSets[index]}
                                                        sizes={SRC_SET_SIZES_TILE}
                                                    />
                                                </div>
                                                <div className={styles.title}>{displayName}</div>
                                            </SbSharedLink>
                                        );
                                    })}
                                </SbRespTilesSwiper>
                            </div>
                        )}
                    </SbSharedCategoryTilesTracking>
                );
            }}
        </ServerVarsContextConsumer>
    );
};

SbRespCategoryTilesComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
};

export const SbRespCategoryTiles = createFragmentContainer(SbRespCategoryTilesComponent, {
    itemSearch: graphql`
        fragment SbRespCategoryTiles_itemSearch on ItemSearchQueryConnection {
            ...SbSharedCategoryTilesTracking_itemSearch
            displayUriRef
            categoryTiles {
                displayName
                rank
                url
                image
                masterImage
            }
        }
    `,
});
